import {DashSearchModules, DashSearchTargets, GenRouteUrls, Keys, ModuleFieldsData, Modules, ThemeTypes} from "app/config/AppConfig";
import {
    checkNull,
    checkNullArr,
    checkNullJson,
    checkNullStr,
    createTimeout,
    formatDate,
    getBrowserCookie,
    getCurrUrlParamValue,
    getJsonValue,
    getDefStr,
    getElement,
    getJsonValueFromNestedKeys,
    getMatchedValueFromObj,
    isBoolTrue,
    isJsonValueTrue,
    mergeListObjects,
    objectHasValue,
    parseInteger,
    populateArr,
    trim
} from "../../common/fusion-ui/utils/ReactUtils";
import {DefStrNA, DefStrNAPlain, THEME} from "../../common/fusion-ui/config/FusionConfig";
import React from "react";
import axios from "axios";
import {AppDateFormat, BaseReqUrl, DebugOn} from "../../common/CommonConfig";

const TAG = "AppUtils";

/**
 * Create Breadcrumbs data format:
 *
 * @example
 * getBreadcrumbData(
 * [name1, name2],
 * [link1, link2],
 * [disable=true, disable=false],
 * [root=true, root=false],
 * )
 *
 * //produced output format
 * const outputFormat=[
 *  {title: 'name', link: 'link', disabled: false, root: false},
 *  {title: 'name', link: 'link', disabled: false, root: false},
 * ];
 */
export function getBreadcrumbData(nameList = [], linkList = [], disableList = [], rootBoolList = []) {
    const out = [];
    let disableUrlList = [];
    let rootUrlList = [];
    disableUrlList = (!checkNullArr(disableList)) ? populateArr(disableUrlList, false) : disableList;
    rootUrlList = (!checkNullArr(rootBoolList)) ? populateArr(rootUrlList, false) : rootBoolList;
    for (let i = 0; i < nameList.length; i++) {
        const single = {
            title: nameList[i], link: linkList[i], disabled: disableUrlList[i], root: rootUrlList[i],
        };
        out.push(single);
    }
    return out;
}

export function isCurrUrlOfAuthRoute(location, currPageUrl, includeLogin = true) {
    try {
        if (!checkNullStr(currPageUrl)) currPageUrl = (getJsonValue(location, 'pathname') + '').toLowerCase();
        let authRoutes = [GenRouteUrls.forgotPw, GenRouteUrls.requestAccess,];
        if (isBoolTrue(includeLogin)) {
            authRoutes.push(GenRouteUrls.login);
        }
        // return authRoutes.includes(currPageUrl);
        return authRoutes.some(url => (currPageUrl + '').toLowerCase().includes((url + '').toLowerCase()));
    } catch (error) {
        console.error('isCurrUrlOfAuthRoute.error:', error);
        return [];
    }
}


/**
 * Clear session-data & token-id from local-Storage.
 */
export function clearUserSessionData(lastUrl, removeLastUrl = false) {
    try {
        localStorage.removeItem(Keys.lsSessionSuperKey);
        if (isBoolTrue(removeLastUrl)) {
            clearRememberLastUrl();
        } else {
            storeRememberLastUrl(lastUrl);
        }
    } catch (error) {
        console.error('clearUserSessionData.error:', error);
    }
}

export function getRememberLastUrl(defUrl = '') {
    const lastUrl = getDefStr(localStorage.getItem(Keys.lastUrlToRemember), defUrl);
    // const lastUrl = getDefStr(unscrambleData(localStorage.getItem(Keys.lastUrlToRemember)), defUrl);
    return isCurrUrlOfAuthRoute(null, lastUrl) ? defUrl : lastUrl;
}

export function isAppThemeDark() {
    return getBrowserCookie(Keys.appTheme) == ThemeTypes.dark.text;
}

export function isAppThemeLight() {
    return !isAppThemeDark();
}

export function storeRememberLastUrl(url = window.location.href) {
    if (checkNullStr(url) && !isCurrUrlOfAuthRoute(null, url)) {
        localStorage.setItem(Keys.lastUrlToRemember, url);
        // localStorage.setItem(Keys.lastUrlToRemember, scrambleData(url));
    }
}

export function clearRememberLastUrl() {
    localStorage.removeItem(Keys.lastUrlToRemember);
}

/**
 * Get session token-id from local-Storage.
 */
export function getUserSessionToken() {
    return getUserSessionField(Keys.sessionToken, null);
}

/**
 * Get a specified user-data key like role-id or created-by etc. from local-Storage.
 */
export function getUserSessionField(fieldKey, defValue) {
    return checkNullArr(fieldKey) ? getJsonValueFromNestedKeys(getUserSessionData(), fieldKey, defValue) : getJsonValue(getUserSessionData(), fieldKey, defValue);
}

/**
 * Get session-data
 */
export function getUserSessionData() {
    try {
        return getJsonValue(JSON.parse(localStorage.getItem(Keys.lsSessionSuperKey)), Keys.sessionData, {});
        // return getJsonValue(JSON.parse(unscrambleData(localStorage.getItem(Keys.lsSessionSuperKey))), Keys.sessionData, {});
    } catch (e) {
        printError(TAG, 'getUserSessionData.e:', e);
        return {};
    }
}

export function getSubdomainSessionData() {
    try {
        return getJsonValue(JSON.parse(localStorage.getItem(Keys.lsSessionSuperKey)), Keys.subdomainData, {});
        // return getJsonValue(JSON.parse(unscrambleData(localStorage.getItem(Keys.lsSessionSuperKey))), Keys.subdomainData, {});
    } catch (e) {
        printError(TAG, 'getSubdomainSessionData.e:', e);
        return {};
    }
}

/**
 * Get a specified domain-data key like role-id or subdomain-name, project-name etc. from local-Storage.
 * @example
 * const companySubdomain = getSubdomainSessionField(ModuleFieldsData[Modules.Company].companySubdomain.column);
 */
export function getSubdomainSessionField(fieldKey, defValue) {
    return checkNullArr(fieldKey)
        ? getJsonValueFromNestedKeys(getSubdomainSessionData(), fieldKey, defValue)
        : getJsonValue(getSubdomainSessionData(), fieldKey, defValue);
}

/**
 * Get allocated modules.
 * @return {array} - Returns an array containing allocated modules in objects.
 *
 * @example
 * //Modules format
 * const allocatedModules = { id: 5, name: 'Module 1', ...rest };
 */
export function getAllocatedModules() {
    const list = getUserSessionField(ModuleFieldsData  [Modules.UserProfile].allocatedModules.column, {});
    return checkNullArr(list) ? list : [];
}

/**
 * Get module's configs like: allocated or not, id, name, alt-name.
 * @param {string|null} moduleName - Module name to check get it's config-data.
 * @param {string|number|null} moduleId - Module-id to get its associate module's config-data.
 * @return {Object} - Returns an object containing module-configs.
 *
 * @example
 * //Returning config-data
 * const moduleConfig = { id: 5, name: 'Module', altName: 'Module-Alt', allocated: true };
 *
 * @hide
 * Data is fetched from "userDbHandler.perms_fetchAllocatedModules" Function & stored in local-storage's session-data.
 */
function getModuleConfigs(moduleName = null, moduleId = null) {
    moduleName = (moduleName + '').toLowerCase().trim();
    moduleId = (moduleId + '').toLowerCase().trim();

    let id = -1;
    let name = '';
    let altName = '';
    let allocated = false;
    const modules = getAllocatedModules();
    for (const module of modules) {
        const mId = (module.id + '').toLowerCase().trim();
        const mName = (module.name + '').toLowerCase().trim();
        const mAltName = (module.nameAlt + '').toLowerCase().trim();
        // allocated = (checkNullStr(moduleName) &&
        //         ((checkNullStr(mName) && (mName == moduleName || mName.includes(moduleName) || moduleName.includes(mName))))
        //         || (checkNullStr(mAltName) && (mAltName == moduleName || mAltName.includes(moduleName) || moduleName.includes(mAltName))))
        //     || (checkNullStr(moduleId) && checkNullStr(mId) && mId == moduleId);
        allocated = (checkNullStr(moduleName) && (checkNullStr(mName) && (mName == moduleName)) || (checkNullStr(mAltName) && (mAltName == moduleName)))
            || (checkNullStr(moduleId) && checkNullStr(mId) && mId == moduleId);
        if (allocated) {
            id = parseInteger(module.id);
            name = module.name;
            altName = module.nameAlt;
            break;
        }
    }
    // printLog('isModuleAllocated.modules:', modules);
    // printLog('isModuleAllocated.moduleName:', moduleName, ' | moduleId:', moduleId, ' | allocated:', allocated,'id:',id,'name:',name,'allocated:',allocated);
    return {id, name, altName, allocated};
}

/**
 * Check if module is allocated or not based on module-name or module-id.
 * @param {string|null} moduleName - Module name to check if it's allocated or not.
 * @param {string|number|null} moduleId - Module-id to check if it's associate module is allocated or not.
 * @return {Boolean} - Returns true if module is allocated, otherwise false.
 */
export function isModuleAllocated(moduleName = null, moduleId = null) {
    // moduleName = (moduleName + '').toLowerCase().trim();
    // moduleId = (moduleId + '').toLowerCase().trim();
    //
    // const modules = getAllocatedModules();
    // const moduleAllocated = modules.some(item => {
    //     const id = (item.id + '').toLowerCase().trim();
    //     const name = (item.name + '').toLowerCase().trim();
    //     const nameAlt = (item.nameAlt + '').toLowerCase().trim();
    //     return (checkNullStr(moduleName) &&
    //             ((checkNullStr(name) && (name == moduleName || name.includes(moduleName) || moduleName.includes(name))))
    //             || (checkNullStr(nameAlt) && (nameAlt == moduleName || nameAlt.includes(moduleName) || moduleName.includes(nameAlt))))
    //         || (checkNullStr(moduleId) && checkNullStr(id) && id == moduleId)
    // });
    // // printLog('isModuleAllocated.modules:', modules);
    // // printLog('isModuleAllocated.moduleName:', moduleName, ' | moduleId:', moduleId, ' | moduleAllocated:', moduleAllocated);
    // return moduleAllocated;
    return getModuleConfigs(moduleName, moduleId).allocated;
}

/**
 * Check if module has an alternative based on module-name or module-id.
 * @param {string|null} moduleName - Module name to check for an alternate name.
 * @param {string|number|null} moduleId - Module-id to check if it's associate module has alternate name.
 * @return {Boolean} - Returns true if module has an alternate-name, otherwise false.
 */
export function checkModuleHasAltName(moduleName = null, moduleId = null) {
    return checkNullStr(getModuleConfigs(moduleName, moduleId).altName);
}

/**
 * Get module's original-name or the alternative-name if it has any.
 * @param {string|null} moduleName - Module name to check for an alternate name.
 * @param {string|number|null} moduleId - Module-id to check if it's associate module has alternate name.
 * @return {string} - Returns the module's original-name if no alternate-name is available, otherwise returns the alternate-name.
 */
export function getAllocatedModuleName(moduleName = null, moduleId = null) {
    const {id, name, altName, allocated} = getModuleConfigs(moduleName, moduleId);
    return checkNullStr(altName) ? altName + '' : name + '';
}

export function isCurrUserRoleAdmin() {
// return getSessionFieldValue('role')==AdminRoleId.admin.name;
//     return true;
    return getUserSessionField('memberUser') == 'admin';
}

export function isCurrUserRoleUser() {
// return getSessionFieldValue('role')==AdminRoleId.user.name;
//     return true;
    return getUserSessionField('memberUser') == 'user';
}

export async function sendRequest({
                                      reqUrl,
                                      reqOptions = {},
                                      redirectOnSessionExpire = true,
                                      timeout = 5000,
                                      type = 'post',
                                      setState,
                                      nestedKeys = [],
                                      tag = TAG,
                                      onFetchBegin,
                                      onFetched,
                                      onError,
                                      fun = 'sendRequest:'
                                  }) {
    return new Promise((resolve, reject) => {
        if (checkNull(onFetchBegin)) onFetchBegin();
        const AXIOS_CONFIG_DATA = {
            // ...checkNullJson(axiosConfig) ? axiosConfig : AxiosConfig,
            baseURL: BaseReqUrl,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                // 'Content-Type': 'application/x-www-form-urlencoded',
                "X-Requested-With": "XMLHttpRequest",
                'Authorization': getUserSessionToken(),
            },
            // authToken: getSessionToken()
            // timeout: timeout,
            // withCredentials: isBoolTrue(withCreds),
        };
        const REQ_OPTIONS = {
            ...checkNullJson(reqOptions) ? reqOptions : {},
            ...(trim(type).toLowerCase() == 'get') ? AXIOS_CONFIG_DATA : {}
        };
        // printLog(tag, `AXIOS_CONFIG_DATA:`, AXIOS_CONFIG_DATA, 'REQ_OPTIONS:', REQ_OPTIONS);

        // return axios[type](reqUrl, (type + '').toLowerCase().trim() == 'get' ? axiosConfig : reqOptions, axiosConfig)
        return axios[type](reqUrl, REQ_OPTIONS, AXIOS_CONFIG_DATA)
            .then(async (res) => {
                const fetchedData = await res.data;

                //check if login is already expired or not
                if (isBoolTrue(redirectOnSessionExpire) && isJsonValueTrue(fetchedData, Keys.loginExpired)) {
                    // printLog(fun, tag, `Session-expired:true`, 'fetchedData:', fetchedData);
                    // clearUserSessionData();
                    // window.location.href = GenRouteUrls.login;
                    return resolve(false);
                }
                const data = getJsonValueFromNestedKeys(fetchedData, nestedKeys, fetchedData);
                if (setState) setState(data);
                if (checkNull(onFetched)) onFetched(fetchedData);
                // printLog(fun,tag, `Request response:`,res);
                // printLog(fun,tag, `Request response fetched-data:`,fetchedData);
                return resolve(data);
            }).catch((err) => {
                printError(fun, tag, err);
                if (checkNull(onError)) onError(err);
                return reject(err);
            })
    });
}

export function focusFormIdElement(id, onlyFocus = false, inputFocusDuration = 3000) {
    try {
        if (checkNullStr(id)) {
            const element = getElement(id, 'id');
            // logTest("focusIdField.element:", element);
            createTimeout(() => {
                try {
                    element.focus();
                } catch (e) {
                    printError(TAG, e);
                }
                if (!isBoolTrue(onlyFocus)) {
                    try {
                        element.classList.remove(THEME);
                        element.classList.add("danger");
                        createTimeout(() => {
                            element.classList.remove("danger");
                            element.classList.add(THEME);
                        }, inputFocusDuration);
                    } catch (e) {
                        printError(TAG, e);
                    }
                }
            }, isBoolTrue(onlyFocus) ? 0 : parseInteger(inputFocusDuration) / 4);
        }
    } catch (e) {
        printError(TAG, e);
    }
}

export function formatDateValue(dateValue, defValue = '', dateFormat = AppDateFormat) {
    let date = (checkNullStr(dateValue) && (dateValue != DefStrNA && dateValue != DefStrNAPlain)) ? formatDate(dateValue, dateFormat) : defValue;
    return (date == '1970-01-01') ? defValue : date;
}


/**
 * Convert a duration-String to a readable duration.
 *
 * @example
 * console.log(formatTime("01:00")); // Output: 1 hr
 * console.log(formatTime("00:45")); // Output: 45 minutes
 * console.log(formatTime("04:22")); // Output: 4:22 hrs
 * console.log(formatTime("00:01")); // Output: 1 minute
 * console.log(formatTime("00:00")); // Output: 00:00 hr
 */
export function formatDurationToReadable(timeString, defValue) {
    if (!checkNullStr(timeString) || (timeString == DefStrNA || timeString == DefStrNAPlain)) return defValue;

    const [hours, minutes] = timeString.split(':').map(Number);

    if (hours === 0 && minutes === 0) {
        return '00:00 hr';
    } else if (hours === 0) {
        return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else if (minutes === 0) {
        return `0${hours}:00 hr`;
    } else {
        return `${hours <= 1 ? '0' + hours : hours}:${minutes <= 1 ? '0' + minutes : minutes} hrs`;
    }
}

/**
 * Returns search-query from browser-url.
 * @param {*} defValue Default value to return if any occur. Default is empty string.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @return {string} Returns search-query string.
 * @example
 * //browser-url: /vok/search?query=search-something&search_id=asdf43e
 * console.log(`search-query: `, getSearchQueryFromUrl()); //output: search-something
 */
export function getSearchQueryFromUrl(defValue = '', urlOptional = '') {
    return getCurrUrlParamValue(Keys.urlParam_SearchQuery, defValue, urlOptional);
}

/**
 * Returns dash-search modules names as an object with indices as keys & module-names as values.
 * @return {object} Object that contains dash-search modules.
 * @example
 * const names = getDashSearchTypeNamesObj();
 * //output:
 * { '0': 'Module 1', '1': 'Module 2', ...rest }
 */
export function getDashSearchTypeNamesObj() {
    return mergeListObjects(Object.values(DashSearchModules).map((item, index) => ({[index]: item.name})));
}

/**
 * Returns dash-search modules ids as an object with indices as keys & module-ids as values.
 * @return {object} Object that contains dash-search modules.
 * @example
 * const names = getDashSearchTypeIdsObj();
 * //output:
 * { '0': 'asdf43e', '1': 'jy5rf', ...rest }
 */
export function getDashSearchTypeIdsObj() {
    return mergeListObjects(Object.values(DashSearchModules).map((item, index) => ({[index]: item.id})));
}

/**
 * Checks if the browser-url's search-type module-id is valid or not.
 * @return {boolean} Returns true if the browser-url's search-type module-id is valid otherwise false.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @example
 * console.log(`isSearchIdValue: ${'/vok/search?query=search-something&search_id=asdf43e'}: `, isUrlSearchTypeIdValid()); //output: true
 * console.log(`isSearchIdValue: ${'/vok/search?query=search-something&search_id=dfgtres'}: `, isUrlSearchTypeIdValid()); //output: false
 */
export function isUrlSearchTypeIdValid(urlOptional = '') {
    return objectHasValue(getDashSearchTypeIdsObj(), getCurrUrlParamValue(Keys.urlParam_SearchTypeId, '', urlOptional), false);
}

/**
 * Returns valid search-type-id from {@link AppUtils.DashSearchModules} if the browser-url's search-type module-id is valid.
 * @param {*} defValue Default value to return if not matched. Default is -1.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @return {string} Returns valid search-type id or def-value if not matched.
 * @example
 * //browser-url: /vok/search?query=search-something&search_id=asdf43e
 * console.log(`search-type-id: `, getValidSearchTypeIdFromUrl()); //output: asdf43e
 */
export function getValidSearchTypeIdFromUrl(defValue = -1, urlOptional = '') {
    const isValid = isUrlSearchTypeIdValid();
    if (isValid) {
        return getMatchedValueFromObj(getDashSearchTypeIdsObj(), getCurrUrlParamValue(Keys.urlParam_SearchTypeId, '', urlOptional), defValue, false);
    } else {
        return defValue;
    }
}

/**
 * Returns valid search-type module-name from {@link AppUtils.DashSearchModules} if the browser-url's search-type module-id is valid.
 * @param {*} defValue Default value to return if not matched. Default is -1.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @return {string} Returns valid search-type module-name or def-module if not matched.
 * @example
 * //browser-url: /vok/search?query=search-something&search_id=asdf43e
 * console.log(`search-type module-name: `, getValidSearchTypeIdNameFromUrl()); //output: Module 1
 */
export function getValidSearchTypeIdNameFromUrl(defValue = -1, urlOptional = '') {
    if (isUrlSearchTypeIdValid()) {
        const idsObj = getDashSearchTypeIdsObj();
        const srcKey = Object.keys(idsObj).find(key => idsObj[key] == getCurrUrlParamValue(Keys.urlParam_SearchTypeId, '', urlOptional));
        return checkNullStr(srcKey) ? getJsonValue(getDashSearchTypeNamesObj(), srcKey, defValue) : defValue;
    } else {
        return defValue;
    }
}


/**
 * Returns dash-search-targets target names as an object with indices as keys & module-names as values.
 * @return {object} Object that contains dash-search targets.
 * @example
 * const names = getDashSearchTargetNamesObj();
 * //output:
 * { '0': 'Target 1', '1': 'Target 2', ...rest }
 */
export function getDashSearchTargetNamesObj() {
    return mergeListObjects(Object.values(DashSearchTargets).map((item, index) => ({[index]: item.name})));
}

/**
 * Returns dash-search targets ids as an object with indices as keys & target-ids as values.
 * @return {object} Object that contains dash-search targets.
 * @example
 * const names = getDashSearchTargetIdsObj();
 * //output:
 * { '0': 'asdf43e', '1': 'jy5rf', ...rest }
 */
export function getDashSearchTargetIdsObj() {
    return mergeListObjects(Object.values(DashSearchTargets).map((item, index) => ({[index]: item.id})));
}

/**
 * Checks if the browser-url's search target-id is valid or not.
 * @return {boolean} Returns true if the browser-url's search target-id is valid otherwise false.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @example
 * console.log(`isSearchIdValue: ${'/vok/search?query=search-something&search_id=asdf43e'}: `, isUrlSearchTargetIdValid()); //output: true
 * console.log(`isSearchIdValue: ${'/vok/search?query=search-something&search_id=dfgtres'}: `, isUrlSearchTargetIdValid()); //output: false
 */
export function isUrlSearchTargetIdValid(urlOptional = '') {
    return objectHasValue(getDashSearchTargetIdsObj(), getCurrUrlParamValue(Keys.urlParam_SearchTargetId, '', urlOptional), false);
}

/**
 * Returns valid search-target-id from {@link AppUtils.DashSearchTargets} if the browser-url's search target-id is valid.
 * @param {*} defValue Default value to return if not matched. Default is -1.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @return {string} Returns valid search-target id or def-value if not matched.
 * @example
 * //browser-url: /vok/search?query=search-something&search_id=asdf43e
 * console.log(`search-target-id: `, getValidSearchTargetIdFromUrl()); //output: asdf43e
 */
export function getValidSearchTargetIdFromUrl(defValue = -1, urlOptional = '') {
    const isValid = isUrlSearchTargetIdValid();
    if (isValid) {
        return getMatchedValueFromObj(getDashSearchTargetIdsObj(), getCurrUrlParamValue(Keys.urlParam_SearchTargetId, '', urlOptional), defValue, false);
    } else {
        return defValue;
    }
}

/**
 * Returns valid search target-name from {@link AppUtils.DashSearchTargets} if the browser-url's search target-id is valid.
 * @param {*} defValue Default value to return if not matched. Default is -1.
 * @param {string} urlOptional Optional URL, if empty the browser url (window.location.href) will be used. Default is empty string.
 * @return {string} Returns valid search target-name or def-target if not matched.
 * @example
 * //browser-url: /vok/search?query=search-something&search_id=asdf43e
 * console.log(`search target-name: `, getValidSearchTargetIdNameFromUrl()); //output: Target 1
 */
export function getValidSearchTargetIdNameFromUrl(defValue = -1, urlOptional = '') {
    if (isUrlSearchTargetIdValid()) {
        const idsObj = getDashSearchTargetIdsObj();
        const srcKey = Object.keys(idsObj).find(key => idsObj[key] == getCurrUrlParamValue(Keys.urlParam_SearchTargetId, '', urlOptional));
        return checkNullStr(srcKey) ? getJsonValue(getDashSearchTargetNamesObj(), srcKey, defValue) : defValue;
    } else {
        return defValue;
    }
}

// export function checkHandlerRoleId(roleId){
//     return HandlerRoles.ADMIN.roleId===roleId;
// }
export function printLog(...logs) {
    if (DebugOn) console.log(...logs);
}

export function printError(...logs) {
    if (DebugOn) console.error(...logs);
}

export function printWarn(...logs) {
    if (DebugOn) console.warn(...logs);
}

export function printInfo(...logs) {
    if (DebugOn) console.info(...logs);
}

export function printDebug(...logs) {
    if (DebugOn) console.debug(...logs);
}
